import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const TermsPrivacyPage: React.FC = () => {
  const { mode } = useCustomTheme();
  const [activeTab, setActiveTab] = useState(0);
  
  // Determine colors based on theme mode
  const isLightMode = mode === 'light';
  const textColor = isLightMode ? '#000000' : '#FFFFFF';
  const secondaryTextColor = isLightMode ? '#4A5568' : '#CCCCCC';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: 3,
            color: textColor,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            letterSpacing: '-0.02em',
          }}
        >
          Terms & Privacy Policy
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: secondaryTextColor,
            maxWidth: 800,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          Please read these terms and privacy policy carefully before using our services
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              color: secondaryTextColor,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
            },
            '& .Mui-selected': {
              color: textColor,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: isLightMode ? '#000000' : '#FFFFFF',
            },
          }}
        >
          <Tab 
            icon={<DescriptionIcon />} 
            label="Terms of Service" 
            iconPosition="start"
          />
          <Tab 
            icon={<SecurityIcon />} 
            label="Privacy Policy" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Terms of Service Tab */}
      {activeTab === 0 && (
        <Box>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              mb: 4,
              background: isLightMode ? '#f8f9fa' : '#1a1a1a',
              borderRadius: 3,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: textColor,
              }}
            >
              Terms of Service
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: secondaryTextColor,
                mb: 4,
              }}
            >
              Last updated: {new Date().toLocaleDateString()}
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: textColor,
              }}
            >
              1. Acceptance of Terms
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: secondaryTextColor,
                mb: 3,
              }}
            >
              By accessing and using the Goat Cards platform, you accept and agree to be bound by the terms and provision of this agreement.
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: textColor,
              }}
            >
              2. Use License
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: secondaryTextColor,
                mb: 3,
              }}
            >
              Permission is granted to temporarily access the materials on Goat Cards for personal, non-commercial transitory viewing only.
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: textColor,
              }}
            >
              3. User Responsibilities
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Provide accurate information"
                  secondary="You must provide accurate and complete information when using our services"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Comply with laws"
                  secondary="You must comply with all applicable laws and regulations"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="No unauthorized use"
                  secondary="You may not use our services for any illegal or unauthorized purpose"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
            </List>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: textColor,
              }}
            >
              4. Disclaimers
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: secondaryTextColor,
                mb: 3,
              }}
            >
              The materials on Goat Cards are provided on an 'as is' basis. Goat Cards makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Privacy Policy Tab */}
      {activeTab === 1 && (
        <Box>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              mb: 4,
              background: isLightMode ? '#f8f9fa' : '#1a1a1a',
              borderRadius: 3,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: textColor,
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: secondaryTextColor,
                mb: 4,
              }}
            >
              Last updated: {new Date().toLocaleDateString()}
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: textColor,
              }}
            >
              Information We Collect
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Personal Information"
                  secondary="Name, email address, and contact information when you create an account"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Usage Data"
                  secondary="Information about how you use our platform and services"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Technical Data"
                  secondary="IP address, browser type, device information, and cookies"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
            </List>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: textColor,
              }}
            >
              How We Use Your Information
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Provide Services"
                  secondary="To provide and maintain our platform and services"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Improve Platform"
                  secondary="To analyze usage patterns and improve our services"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Communication"
                  secondary="To send you important updates and respond to inquiries"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
            </List>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: textColor,
              }}
            >
              Data Protection
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon sx={{ fontSize: 40, color: isLightMode ? '#000000' : '#FFFFFF', mr: 2 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: textColor,
                }}
              >
                Security Measures
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: secondaryTextColor,
                mb: 3,
              }}
            >
              We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure servers, and regular security assessments.
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: textColor,
              }}
            >
              Your Rights
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Access Your Data"
                  secondary="You can request access to your personal information"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Update Information"
                  secondary="You can update or correct your personal information"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon sx={{ color: isLightMode ? '#000000' : '#FFFFFF' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Delete Account"
                  secondary="You can request deletion of your account and data"
                  primaryTypographyProps={{ color: textColor, fontWeight: 600 }}
                  secondaryTypographyProps={{ color: secondaryTextColor }}
                />
              </ListItem>
            </List>
          </Paper>
        </Box>
      )}

      {/* Contact Information */}
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: textColor,
          }}
        >
          Questions or Concerns?
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            color: secondaryTextColor,
            mb: 2,
          }}
        >
          If you have any questions about these terms or privacy policy, please contact us:
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: secondaryTextColor,
            fontWeight: 600,
          }}
        >
          privacy@goatcards.shop
        </Typography>
      </Box>
    </Container>
  );
};

export default TermsPrivacyPage; 